import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Layout } from "./Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function VisitorAccessPage() {
  const { token, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showPassword, setShowPassword] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const api = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });

  const fetchAccess = async () => {
    try {
      const res = await api.get("/admin/visitor-access");
      setData(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de charger les informations visiteur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchAccess();
    else setLoading(false);
  }, [isAdmin]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await api.post("/admin/visitor-access/regenerate");
      setData(res.data);
      setShowPassword(true);
      toast.success("Mot de passe régénéré");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la régénération");
    } finally {
      setRegenerating(false);
      setConfirmOpen(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copié`);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto py-20 text-center" data-testid="visitor-access-denied">
          <h1 className="font-heading text-2xl mb-2">Accès refusé</h1>
          <p className="text-sm text-muted-foreground">Cette page est réservée aux administrateurs.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl space-y-8" data-testid="visitor-access-page">
        <header>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Administration</p>
          <h1 className="font-heading text-4xl">Accès visiteur</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Identifiants partagés du compte en lecture seule. Le mot de passe est régénéré automatiquement tous les <strong>{data?.period_days ?? 15} jours</strong>.
          </p>
        </header>

        <div className="bg-white border border-border">
          {/* Email row */}
          <div className="flex items-center justify-between gap-4 p-5 border-b border-border">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Email</p>
              <p className="font-heading text-lg break-all" data-testid="visitor-email">{data?.email}</p>
            </div>
            <button
              onClick={() => copy(data?.email || "", "Email")}
              className="border border-border px-4 py-2 text-xs uppercase tracking-wider hover:bg-muted transition-colors flex-shrink-0"
              data-testid="copy-email-btn"
            >
              Copier
            </button>
          </div>

          {/* Password row */}
          <div className="flex items-center justify-between gap-4 p-5 border-b border-border">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Mot de passe actuel</p>
              <p className="font-mono text-lg tracking-wider" data-testid="visitor-password">
                {showPassword ? (data?.password || "—") : "•".repeat((data?.password || "").length)}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setShowPassword((s) => !s)}
                className="border border-border px-3 py-2 text-xs uppercase tracking-wider hover:bg-muted transition-colors"
                data-testid="toggle-password-visibility-btn"
                title={showPassword ? "Masquer" : "Afficher"}
              >
                {showPassword ? "Masquer" : "Afficher"}
              </button>
              <button
                onClick={() => copy(data?.password || "", "Mot de passe")}
                className="border border-border px-4 py-2 text-xs uppercase tracking-wider hover:bg-muted transition-colors"
                data-testid="copy-password-btn"
              >
                Copier
              </button>
            </div>
          </div>

          {/* Dates row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border">
            <div className="bg-white p-5">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Dernière régénération</p>
              <p className="font-heading text-base" data-testid="last-regen-date">
                {formatDate(data?.last_regenerated_at)}
              </p>
            </div>
            <div className="bg-white p-5">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Prochaine régénération automatique</p>
              <p className="font-heading text-base" data-testid="next-regen-date">
                {formatDate(data?.next_regeneration_at)}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 p-5 border border-dashed border-border bg-muted/30">
          <div>
            <p className="font-heading text-sm">Régénérer manuellement</p>
            <p className="text-xs text-muted-foreground mt-1">
              Un nouveau mot de passe sera généré et l&apos;échéance automatique sera décalée à J+{data?.period_days ?? 15}.
            </p>
          </div>
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={regenerating}
            className="bg-foreground text-white px-6 py-2.5 uppercase tracking-wider text-sm hover:bg-foreground/90 disabled:opacity-50 flex-shrink-0"
            data-testid="regenerate-password-btn"
          >
            {regenerating ? "Régénération…" : "Régénérer"}
          </button>
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmOpen(false)}>
          <div className="bg-white p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()} data-testid="regen-confirm-modal">
            <h2 className="font-heading text-xl mb-3">Régénérer le mot de passe ?</h2>
            <p className="text-sm text-muted-foreground mb-4">
              L&apos;ancien mot de passe ne sera plus valide. Le visiteur devra utiliser le nouveau.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="flex-1 bg-foreground text-white px-4 py-2.5 text-sm uppercase tracking-wider hover:bg-foreground/90 disabled:opacity-50"
                data-testid="confirm-regenerate-btn"
              >
                {regenerating ? "…" : "Confirmer"}
              </button>
              <button
                onClick={() => setConfirmOpen(false)}
                className="flex-1 border border-border px-4 py-2.5 text-sm hover:bg-muted"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
